import React from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InboxIcon from '@mui/icons-material/Inbox';
import { SecretCard } from './SecretCard';
import { useKeyStore } from '@/stores/keyStore';
import type { Secret } from '@/types/api.types';

interface SecretsListProps {
  secrets: Secret[];
  onDownload: (secretId: string) => void;
  onDelete: (secretId: string) => void;
  downloadingSecretId: string | null;
  deletingIds: Set<string>;
}

type FilterType = 'all' | 'decryptable' | 'expiring';

/**
 * List/grid view of received secrets with filtering
 */
export const SecretsList: React.FC<SecretsListProps> = ({
  secrets,
  onDownload,
  onDelete,
  downloadingSecretId,
  deletingIds,
}) => {
  const { currentKey } = useKeyStore();
  const [filter, setFilter] = React.useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [now, setNow] = React.useState(() => Date.now());

  // Update now on interval for expiring filter
  React.useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  const filteredSecrets = React.useMemo(() => {
    let filtered = [...secrets];

    // Apply filter
    if (filter === 'decryptable') {
      const currentFingerprint = currentKey?.fingerprint;
      filtered = filtered.filter((s) =>
        !s.recipient_key_fingerprint ||
        s.recipient_key_fingerprint === currentFingerprint
      );
    } else if (filter === 'expiring') {
      filtered = filtered.filter((s) => {
        if (!s.expiration) return false;
        const expirationDate = new Date(s.expiration);
        const hoursUntilExpiration =
          (expirationDate.getTime() - now) / (1000 * 60 * 60);
        return hoursUntilExpiration < 24 && hoursUntilExpiration > 0;
      });
    }

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          (s.sender_email || s.sender || '').toLowerCase().includes(query) ||
          s.reference.toLowerCase().includes(query)
      );
    }

    // Sort by created date (newest first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at || a.sent_at).getTime();
      const dateB = new Date(b.created_at || b.sent_at).getTime();
      return dateB - dateA;
    });

    return filtered;
  }, [secrets, filter, searchQuery, currentKey?.fingerprint, now]);

  if (secrets.length === 0) {
    return (
      <Paper
        sx={{
          p: 6,
          textAlign: 'center',
          bgcolor: 'background.paper',
        }}
      >
        <InboxIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h5" color="text.secondary" gutterBottom>
          No secrets received yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Secrets sent to you will appear here
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search by sender or reference..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, minWidth: 200 }}
          size="small"
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Filter</InputLabel>
          <Select
            value={filter}
            label="Filter"
            onChange={(e) => setFilter(e.target.value as FilterType)}
          >
            <MenuItem value="all">All Secrets</MenuItem>
            <MenuItem value="decryptable">Decryptable</MenuItem>
            <MenuItem value="expiring">Expiring Soon</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {filteredSecrets.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No secrets match your filters
          </Typography>
        </Paper>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing {filteredSecrets.length} of {secrets.length} secrets
          </Typography>

          <Grid container spacing={2} sx={{ width: '100%', margin: 0, alignItems: 'stretch' }}>
            {filteredSecrets.map((secret) => (
              <Grid key={secret.reference} size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: 'flex' }}>
                <Box
                  sx={{
                    transition: 'opacity 350ms ease-in, transform 350ms ease-in, max-height 380ms ease-in',
                    opacity: deletingIds.has(secret.reference) ? 0 : 1,
                    transform: deletingIds.has(secret.reference) ? 'scale(0.97)' : 'scale(1)',
                    maxHeight: deletingIds.has(secret.reference) ? 0 : '600px',
                    overflow: 'hidden',
                    paddingTop: '4px',
                    width: '100%',
                    display: 'flex',
                  }}
                >
                  <SecretCard
                    secret={secret}
                    onDownload={onDownload}
                    onDelete={onDelete}
                    isDownloading={downloadingSecretId === secret.id}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};
